import { Card, CardContent, Box, Typography, Grid } from "@mui/material";
import { Business as BusinessIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import LocationDetails from "./LocationDetails";
import ContactPersonDetails from "./ContactPersonDetails";
import ServicePricesDetails from "./ServicePricesDetails";
import FieldDiff from "./FieldDiff";
import { IChangeReview, isFieldDiff } from "../../../../../types/review";
import ImageDiff from "../../../../../components/diff/ImageDiff";

interface CarWashDetailsCardProps {
  changes: IChangeReview["changes"];
  onViewMap?: () => void;
}

const CarWashDetailsCard = ({
  changes,
  onViewMap,
}: CarWashDetailsCardProps) => {
  const { t } = useTranslation();
  return (
    <>
      <Grid size={12}>
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <BusinessIcon sx={{ mr: 1 }} />
              <Typography variant="h6">{t("carWashDetails.title")}</Typography>
            </Box>
            <Grid container spacing={2}>
              {changes.legalName && (
                <Grid size={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("carWashDetails.legalName")}
                  </Typography>
                  {isFieldDiff(changes.legalName as any) ? (
                    <FieldDiff diff={changes.legalName as any} />
                  ) : (
                    <Typography variant="body1">
                      {changes.legalName as any}
                    </Typography>
                  )}
                </Grid>
              )}
              {/* Parking notes. Collected on the Partner "Add Car Wash" form
                  since it shipped, but never rendered here — so an Owner who
                  edited only this field showed an admin an empty diff. */}
              {changes.parkingDescription && (
                <Grid size={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("carWashDetails.parkingDescription")}
                  </Typography>
                  {isFieldDiff(changes.parkingDescription as any) ? (
                    <FieldDiff diff={changes.parkingDescription as any} />
                  ) : (
                    <Typography variant="body1">
                      {changes.parkingDescription as any}
                    </Typography>
                  )}
                </Grid>
              )}
              {changes.location && (
                <LocationDetails
                  location={changes.location}
                  onViewMap={onViewMap}
                />
              )}
              {changes.contact && (
                <ContactPersonDetails contact={changes.contact} />
              )}
              {/* Colour-coded gallery: kept photos plain, added ones green,
                  removed ones red and greyed. Replaces a flat thumbnail grid
                  that showed only the resulting set — and which would have
                  rendered nothing at all once an edit started recording
                  images as {old, new} rather than a bare array. */}
              {changes.images && (
                <Grid size={12}>
                  <ImageDiff images={changes.images as any} />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {changes.services && (
        <ServicePricesDetails
          services={changes.services}
          title={t("carWashDetails.servicePrices")}
        />
      )}
    </>
  );
};

export default CarWashDetailsCard;
